import { concat, find, flatMap } from 'lodash/fp';
import { PortableText } from '@ahmdigital/ui';
import React from 'react';
import timezonePlaceholderSerializer from '@ahmdigital/logic/lib/utils/sanity-timezone-placeholder-serializer';

import { optionConstantIds } from '../../../../contact-options';
import { PortableTextType, TableOfContentsItem } from '../../../../../../../types/sanityInternal';
import ahmConstants from '../../../../../../../ahm-constants';
import styles from './styles.module.sass';
import type { Placeholder } from '../../../../../../../components/offers/types';

export const CONTACT_OPTION_ID = {
  CURRENT_MEMBER: 'current member',
  LOOKING_TO_JOIN: 'Get in touch about joining',
};

const getSubText = ({ placeholders, source }: { placeholders: Placeholder[]; source: PortableTextType[] }) => [
  <PortableText
    source={source}
    typeSerializers={{
      placeholder: timezonePlaceholderSerializer({ placeholders }),
    }}
    variant={styles.withoutMargin}
  />,
];

type Props = {
  appendCustomOptions?: boolean;
  customOptions?: {
    contactOptionId: string;
  }[];
  id: string;
  items: TableOfContentsItem[];
  placeholders: Placeholder[];
  title: string;
};

const formatContactOptions = ({
  appendCustomOptions = false,
  customOptions = [],
  id,
  items,
  placeholders,
  title,
}: Props) => {
  const contacts = flatMap('item', items);
  const isLookingToJoin = id === CONTACT_OPTION_ID.LOOKING_TO_JOIN;

  const chatOfflineItem = find({ type: 'chat-offline' }, contacts);
  const chatOnlineItem = find({ type: 'chat-online' }, contacts);
  const chatOption = {
    contactOptionData: {
      offline: chatOfflineItem
        ? {
            buttonText: chatOfflineItem.action,
            buttonUrl: `sms:${chatOfflineItem.phoneNumber}`,
            subText: getSubText({ placeholders, source: chatOfflineItem.descriptionRaw }),
            title: chatOfflineItem.title,
          }
        : {
            buttonText: 'Leave a message',
            buttonUrl: `sms:${ahmConstants.CONTACT.PHONE.SMS_STANDARD}`,
            subText: `Thinking about joining ahm? Send us a message and we’ll get back to you as soon as we’re back online. ${ahmConstants.CONTACT.PHONE.SMS_PRETTY}`,
            title: 'SMS us',
          },
      online: chatOnlineItem
        ? {
            buttonText: chatOnlineItem.action,
            subText: getSubText({ placeholders, source: chatOnlineItem.descriptionRaw }),
            title: chatOnlineItem.title,
          }
        : {
            buttonText: 'Start chat',
            subText: '8am – 7pm Monday to Friday (AET).',
            title: 'Message us',
          },
    },
    contactOptionId: isLookingToJoin ? optionConstantIds.lookingToJoinChatBox : optionConstantIds.currentMemberChatBox,
  };

  const callUsItem = find({ type: 'phone' }, contacts);
  const callUsOption = {
    contactOptionData: callUsItem
      ? {
          buttonText: callUsItem.action,
          buttonUrl: `tel:${callUsItem.phoneNumber}`,
          subText: getSubText({ placeholders, source: callUsItem.descriptionRaw }),
          title: callUsItem.title,
        }
      : {
          buttonText: `Call ${ahmConstants.CONTACT.PHONE.PRETTY}`,
          buttonUrl: `tel:${ahmConstants.CONTACT.PHONE.STANDARD}`,
          subText: [
            `${ahmConstants.CONTACT.PHONE.PRETTY}`,
            '9am – 6pm Monday to Friday (AEST).',
            `Calling from overseas? ${ahmConstants.CONTACT.PHONE.INTERNATIONAL_PRETTY}`,
          ],
          title: 'Call us',
        },
    contactOptionId: optionConstantIds.callUs,
  };

  return {
    id,
    options: appendCustomOptions
      ? concat([chatOption, callUsOption], customOptions)
      : concat(customOptions, [chatOption, callUsOption]),
    showAlertMessage: false,
    title,
  };
};

export default formatContactOptions;
